import type { StoreApi } from 'zustand';
import { getAdminApiError } from '../lib/adminApiErrors';
import {
  getDocumentConflictCurrent,
  mustClearSensitiveAdminState,
} from '../lib/legalAdminErrors';
import { createAdminIdempotencyKey } from '../lib/adminIdempotency';
import type {
  AdminDocumentPackageDetail,
  ETagged,
  LegalPackageCommand,
  SignedArtifactUploadInput,
} from '../legalTypes';
import type { SuperAdminActions, SuperAdminState } from './useSuperAdminStore';

export type AdminStore = SuperAdminState & SuperAdminActions;
export type SetAdminStore = StoreApi<AdminStore>['setState'];
export type GetAdminStore = StoreApi<AdminStore>['getState'];
export type DocumentActions = Pick<
  SuperAdminActions,
  | 'fetchDocumentPackages'
  | 'resolveDocumentPackageCompany'
  | 'loadDocumentWorkspace'
  | 'clearDocumentWorkspace'
  | 'loadCompanyLegalSummary'
  | 'clearCompanyLegalSummary'
  | 'saveLegalProfile'
  | 'refreshLegalProfileConflict'
  | 'executeDocumentCommand'
  | 'refreshDocumentConflict'
  | 'configureDocumentSigners'
  | 'executeDocumentSignerCommand'
  | 'completeDocumentSigner'
  | 'uploadDocumentEvidence'
  | 'downloadDocumentArtifact'
  | 'downloadDocumentPackage'
>;

export interface DocumentRuntime {
  detailGeneration: number;
  mutationIdentity: { fingerprint: string; key: string } | null;
  listController: AbortController | null;
  summaryGeneration: number;
}

export const clearSensitiveDocumentState = (
  set: SetAdminStore,
  runtime: DocumentRuntime
) => {
  runtime.detailGeneration += 1;
  runtime.summaryGeneration += 1;
  runtime.listController?.abort();
  runtime.listController = null;
  runtime.mutationIdentity = null;
  set({
    documentPackages: [],
    documentPackagePage: null,
    documentPackage: null,
    documentBundle: null,
    legalProfile: null,
    legalProfileConflict: null,
    documentListLoading: false,
    documentDetailLoading: false,
    documentMutationLoading: false,
    documentPartialError: null,
    documentConflict: null,
    companyLegalSummary: null,
    companyLegalSummaryLoading: false,
    companyLegalSummaryError: null,
  });
};

export const updateCanonicalPackage = (
  set: SetAdminStore,
  canonical: ETagged<AdminDocumentPackageDetail>
) => set((state) => ({
  documentPackage: canonical,
  documentPackages: state.documentPackages.map((item) => item.id === canonical.data.id
    ? {
      ...item,
      state: canonical.data.state,
      updatedAtUtc: canonical.data.updatedAtUtc,
      version: canonical.data.version,
    }
    : item),
}));

export const supportsDocumentCommand = (
  detail: AdminDocumentPackageDetail,
  command: LegalPackageCommand
): boolean => detail.availableCommands.some((candidate) => {
  const normalized = candidate.replace(/([a-z])([A-Z])/g, '$1_$2').toLocaleLowerCase();
  return normalized === command
    || (command === 'regenerate' && normalized === 'regenerate_before_approval');
});

export const documentMutationKey = (
  runtime: DocumentRuntime,
  fingerprint: string
): string => {
  if (runtime.mutationIdentity?.fingerprint !== fingerprint) {
    runtime.mutationIdentity = {
      fingerprint,
      key: createAdminIdempotencyKey(),
    };
  }
  return runtime.mutationIdentity.key;
};

const readFileBytes = (file: File): Promise<ArrayBuffer> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(reader.error ?? new Error('The selected file could not be read.'));
  reader.onload = () => reader.result instanceof ArrayBuffer
    ? resolve(reader.result)
    : reject(new Error('The selected file could not be read.'));
  reader.readAsArrayBuffer(file);
});

const sha256File = async (file: File): Promise<string> => {
  if (!globalThis.crypto?.subtle) {
    throw Object.assign(new Error('Secure upload fingerprinting is unavailable.'), {
      code: 'upload_fingerprint_unavailable',
    });
  }
  const digest = await globalThis.crypto.subtle.digest('SHA-256', await readFileBytes(file));
  let hex = '';
  for (const byte of new Uint8Array(digest)) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
};

export const createUploadFingerprint = async (
  current: ETagged<AdminDocumentPackageDetail>,
  signerId: string,
  input: SignedArtifactUploadInput
): Promise<string> => JSON.stringify({
  operation: 'upload_signed_artifact',
  companyId: current.data.companyId,
  packageId: current.data.id,
  signerId,
  etag: current.etag,
  fileName: input.file.name.trim(),
  mimeType: input.file.type.trim().toLowerCase(),
  size: input.file.size,
  sha256: await sha256File(input.file),
  declaredSignatoryName: input.declaredSignatoryName.trim(),
  authorityConfirmed: input.authorityConfirmed,
  signatureMethod: input.signatureMethod.trim(),
});

export const applyDocumentMutationFailure = (
  set: SetAdminStore,
  runtime: DocumentRuntime,
  current: ETagged<AdminDocumentPackageDetail>,
  error: unknown,
  fallbackCode: string
) => {
  const failure = getAdminApiError(error, fallbackCode);
  if (mustClearSensitiveAdminState(error)) {
    clearSensitiveDocumentState(set, runtime);
    set({ documentMutationLoading: false, documentError: failure });
    return;
  }
  const serverCurrent = getDocumentConflictCurrent(error);
  set({
    documentMutationLoading: false,
    documentError: failure,
    documentConflict: serverCurrent
      ? { error: failure, local: current.data, current: serverCurrent }
      : null,
  });
};

type CompanyScopedDocument = ETagged<
  AdminDocumentPackageDetail & { companyId: number }
>;

export const requireCurrentDocument = (get: GetAdminStore): CompanyScopedDocument => {
  const current = get().documentPackage;
  if (!current || current.data.companyId === null) {
    throw Object.assign(new Error('Select a document package before running lifecycle actions.'), {
      code: 'document_required',
    });
  }
  if (current.etag.trim().length === 0) {
    throw Object.assign(new Error('Refresh the selected package before running lifecycle actions.'), {
      code: 'etag_required',
    });
  }
  return current as CompanyScopedDocument;
};
